import type { FollowUpItemType } from '../services/followUps.service';

/**
 * Builders for the identity + label of a Follow Up entry.
 *
 * These exist because more than one component bookmarks the same record: a pro
 * is bookmarked from ProDetail's PageHeader in page mode AND from
 * ProDetailSlideOver's chrome in drawer mode, and both write the same
 * (user, type, item_id) row. Building the id in two places invites them to
 * disagree — one pro occupying two panel entries, or a label that flips
 * depending on which surface it was last bookmarked from.
 */

export interface FollowUpTarget {
  type: FollowUpItemType;
  itemId: string;
  label: string;
}

/** `·` between the identifier and the human part. */
const SEP = ' · ';

/**
 * Names come off the order/quote record and occasionally carry stray newlines
 * or double spaces; a newline would break the panel's single-line row.
 */
const tidy = (s: string | null | undefined): string => (s ?? '').replace(/\s+/g, ' ').trim();

const withName = (head: string, name: string | null | undefined): string => {
  const n = tidy(name);
  return n ? `${head}${SEP}${n}` : head;
};

/**
 * A pro or a template — one builder, because they are one record type. ProPage
 * derives templateness from a `T` pronumber prefix, so deriving it here from
 * the same prefix means there is exactly one rule and nothing to keep in sync.
 *
 * The id keeps its original case: normalising it would split one template
 * across two rows depending on how the caller happened to spell it.
 */
export const proFollowUp = (
  pronumber: number | string | null | undefined,
  customerName: string | null | undefined,
): FollowUpTarget | null => {
  const id = String(pronumber ?? '').trim();
  // `0` is the create-mode placeholder pronumber, not a record.
  if (!id || id === '0') return null;
  // A real identity is digits (an order) or T + digits (a template) and nothing
  // else. ProDetail passes pronumber="NEW" until the order is allocated one, and
  // without this that would become a permanent panel entry pointing at /pro/NEW.
  if (!/^[Tt]?\d+$/.test(id)) return null;

  const isTemplate = id.charAt(0).toUpperCase() === 'T';
  return {
    type: isTemplate ? 'template' : 'pro',
    itemId: id,
    label: withName(`${isTemplate ? 'Template' : 'PRO'} ${id}`, customerName),
  };
};

/**
 * A quote is identified by number AND revision — `43448-11` is the shape
 * /order-management/quotes/:quoteId parses, so both are required.
 */
export const quoteFollowUp = (
  quoteNumber: number | string | null | undefined,
  revisionNumber: number | string | null | undefined,
  customerName: string | null | undefined,
): FollowUpTarget | null => {
  const q = String(quoteNumber ?? '').trim();
  // Revision 0 is a real revision, so only null/undefined/'' disqualify it.
  const r = revisionNumber === null || revisionNumber === undefined
    ? ''
    : String(revisionNumber).trim();
  if (!q || !r) return null;

  const id = `${q}-${r}`;
  return { type: 'quote', itemId: id, label: withName(`Quote ${id}`, customerName) };
};

/**
 * A driver is identified by unit number — what DriverLoadsDrawerV2 takes.
 * `000` is the bypass pseudo-unit with neither a safety record nor moves
 * (see DriverIconLinks), so bookmarking it would put a dead entry on the panel.
 */
export const driverFollowUp = (
  unitNumber: string | null | undefined,
  driverName: string | null | undefined,
): FollowUpTarget | null => {
  const id = String(unitNumber ?? '').trim();
  if (!id || id === '000') return null;

  return { type: 'driver', itemId: id, label: withName(`Driver ${id}`, driverName) };
};
